import React, { useMemo } from 'react';
import styled from "styled-components";
import Colours from "../atoms/Colours";
import { BodyRegular, BodyVerySmall } from "../atoms/fonts/Body";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectAllOfficeEntities } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import { Pill } from "../atoms/Pill";
import Icon, { IconTypes } from "../atoms/icon/Icon";
import IconButton from "./icon-button/IconButton";
import { Checkbox } from "../atoms/Checkbox";
import { Link } from "react-router-dom";
import { selectIsInTeams } from '@shared/store/auth.duck';

function TeamContentCardInner(props: Props) {
  const {officeIds, showEditIcon, disabled} = props;
  const {t} = useTranslation();
  const allOffices = useSelector(selectAllOfficeEntities);
  const isInTeams = useSelector(selectIsInTeams);

  const linkedOffices = useMemo(() => {
    if (!officeIds) return [];
    return allOffices.filter((o: OfficeEntity) => officeIds.includes(o.id));
  }, [allOffices, officeIds]);

  const onCheckboxChange = (val: boolean) => {
    if (props.onCheckboxChecked) {
      props.onCheckboxChecked(val);
    }
  }

  const onEditClick = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    if (!!props.onEditClick) {
      props.onEditClick();
    }
  }

  return (
    <CompanyViewContentCardWrapper disabled={disabled}>

      {disabled && <>
        <Icon icon={IconTypes.Padlock} className={'teamContentCard__lock'} />
      </>}

      {showEditIcon && <>
        {!!props.editLink && !isInTeams && <EditButton>
          <IconButton icon={IconTypes.Edit} color={'darkGrey'} link={props.editLink} />
        </EditButton>}
        {!!props.onEditClick && <EditButton onClick={onEditClick}>
          <IconButton icon={IconTypes.Edit} color={'darkGrey'} onClick={onEditClick} />
        </EditButton>}
      </>}

      <BodyRegular weight={600} className={"teamContentCard__title"}>{props.label}</BodyRegular>
      <BodyRegular weight={400} className={"teamContentCard__subTitle"}>{props.memberCount} {t('general.team-members')}</BodyRegular>
      {linkedOffices.map((lo: OfficeEntity, key: number) => (
        <Pill key={key} colour={Colours.black}
              title={lo.label}
              style={{marginLeft: 0, border: 'none'}}
              backgroundColour={'#E6FCFF'}
              className={'teamContentCard__officePill'}>
          <Icon icon={IconTypes.Office} />
          <BodyVerySmall weight={400}>{lo.label}</BodyVerySmall>
        </Pill>))}
      {props?.checkboxEnabled && <Checkbox className="teamContentCard__checkbox"
                                           value={!!props?.isChecked}
                                           onChange={(val: boolean) => onCheckboxChange(val)} />}
    </CompanyViewContentCardWrapper>
  )
}

export function TeamContentCard(props: Props) {
  const {link, disabled} = props;

  if (link && !disabled) {
    return (<Link to={link}>
      <TeamContentCardInner {...props} />
    </Link>)
  } else {
    return (<TeamContentCardInner {...props} />)
  }
}

interface Props {
  link: string;
  label: string;
  editLink?: string;
  showEditIcon: boolean;
  memberCount: number
  officeIds?: number[];
  checkboxEnabled?: boolean;
  onCheckboxChecked?: (val: boolean) => any;
  onEditClick?: () => any;
  isChecked?: boolean;
  disabled?: boolean;
}

const EditButton = styled.div`
  position: absolute;
  bottom: 4px;
  right: 0;
`

export const CompanyViewContentCardWrapper = styled.div<any>`
  position: relative;
  padding: 24px;
  box-shadow: inset 0 0 0 1px ${Colours.mildGrey};
  border-radius: 12px;
  width: 100%;
  min-height: 148px;
  cursor: ${props => props.disabled ? 'auto' : 'pointer'};
  background-color: ${props => props.disabled ? Colours.veryLightGrey : Colours.white};
  &:hover {
    box-shadow: ${props => props.disabled ? `inset 0 0 0 1px ${Colours.mildGrey}` : `inset 0 0 0 2px ${Colours.blue}` };
  }
  .teamContentCard__title {
    color: ${props => props.disabled ? Colours.darkGrey : Colours.black};
  }
  .teamContentCard__subTitle {
    color: ${props => props.disabled ? Colours.darkGrey : Colours.black};
  }
  .teamContentCard__officePill {
    max-width: 125px;
    margin-right: 8px;
    padding: 4px 8px 4px 12px;
    p {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
    i {
      font-size: 16px;
      margin-right: 6px;
    }
  }
  .teamContentCard__checkbox {
    position: absolute;
    top: 16px;
    right: 4px;
  }
  .teamContentCard__lock {
    position: absolute;
    top: 16px;
    right: 16px;
    font-size: 24px;
    color: ${props => props.disabled ? Colours.darkGrey : Colours.black};
  }
`
